import { ButtonMain } from '@quero-delivery/quero-components-web';
import * as Form from '@radix-ui/react-form';
import {
  getEndOfDay,
  getStartOfDay,
  isSameHour,
} from '@querodelivery/qd-date-helper';
import debounce from 'lodash/debounce';
import { useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useMotoboys } from '../../hooks/use-motoboys';
import useScreenType from '@/hooks/util/useScreenType';
import {
  BILLING_TYPE_OPTIONS,
  DELIVERIES_FORM_SHECMA,
  GET_DELIVERIES_LIST,
  STATUS_OPTIONS,
  TYPE_DELIVERY_OPTIONS,
} from '../../constants';
import styles from './styles.module.scss';
import { usePlaces } from '../../../../_hooks/use-places';
import { stringifyQueryStringArray } from '@/Utils/FnUtils';
import type { AccountInfoFormType, DeliveriesFilterProps } from './types';
import { type GetDeliveriesListParams } from '../../types';
import { useAppDispatch } from '@/_store/hooks';
import {
  setFilterCityOnDeliveriesList,
  setFilterDeliveryTypeOnDeliveriesList,
} from '@/_store/modules/filters/slice';
import { useCities } from '@/_hooks/use-cities';
import { TextInput } from '@/_components/text-input/text-input';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { CalendarInput } from '@/_components/calendar-input/calendar-input';

export function DeliveriesFilter({
  params,
  setParams,
  setPage,
  isLoading,
}: DeliveriesFilterProps) {
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();

  const isMobile = useScreenType('mobile');

  const [namePlaceDebouce, setPlaceDebouce] = useState('');

  const [nameDeliveryManDebouce, setNameDeliveryManDebouce] = useState('');

  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AccountInfoFormType>({
    resolver: yupResolver(DELIVERIES_FORM_SHECMA),
    defaultValues: params,
  });

  const debounceNameDeliveryManUpdate = useRef(
    debounce(setNameDeliveryManDebouce, 1000),
  );

  const debouncePlaceUpdate = useRef(debounce(setPlaceDebouce, 1000));

  const municipioId = watch('cityId');
  const selectedPlaceId = watch('placeId');
  const selectedPlaceName = watch('placeName');
  const selectedMotoboyId = watch('motoboyId');
  const selectedMotoboyName = watch('motoboyName');

  const { getMotoboys, motoboysOptions } = useMotoboys({
    nameDeliveryMan: nameDeliveryManDebouce,
    municipioId,
  });

  const { isFetching, placesOptions } = usePlaces({
    placeName: namePlaceDebouce,
  });

  const placeDropdownOptions = useMemo(() => {
    if (
      selectedPlaceId &&
      selectedPlaceName &&
      !placesOptions.some(option => option.value === selectedPlaceId)
    ) {
      return [
        {
          key: selectedPlaceId,
          text: selectedPlaceName,
          value: selectedPlaceId,
        },
        ...placesOptions,
      ];
    }

    return placesOptions;
  }, [placesOptions, selectedPlaceId, selectedPlaceName]);

  const motoboyDropdownOptions = useMemo(() => {
    if (
      selectedMotoboyId &&
      selectedMotoboyName &&
      !motoboysOptions.some(option => option.value === selectedMotoboyId)
    ) {
      return [
        {
          key: selectedMotoboyId,
          text: selectedMotoboyName,
          value: selectedMotoboyId,
        },
        ...motoboysOptions,
      ];
    }

    return motoboysOptions;
  }, [motoboysOptions, selectedMotoboyId, selectedMotoboyName]);

  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const { CITIES_OPTIONS, loadingCities } = useCities();

  function submitForm(data: AccountInfoFormType) {
    const {
      cityId,
      code,
      endDate,
      startDate,
      status,
      deliveryId,
      placeId,
      placeName,
      motoboyId,
      motoboyName,
      deliveryType,
      billingType,
    } = data;

    const queries: GetDeliveriesListParams = {
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      code,
      cityId,
      deliveryId,
      status,
      placeId,
      placeName,
      motoboyId,
      motoboyName,
      deliveryType,
      billingType,
    };

    queryClient.invalidateQueries([GET_DELIVERIES_LIST, queries]);

    setParams(queries);
    setSearchParams(
      stringifyQueryStringArray({
        ...queries,
        startDate: startDate?.toISOString() || '',
        endDate: endDate?.toISOString() || '',
      }),
    );

    setPage(1);
  }

  function handleChangeDate(
    newDate: Date | null,
    onChange: (...event: unknown[]) => void,
    name: string,
  ) {
    if (!newDate) {
      return;
    }

    if (isSameHour(newDate, getStartOfDay(newDate))) {
      return onChange(
        name === 'startDate' ? getStartOfDay(newDate) : getEndOfDay(newDate),
      );
    }

    onChange(newDate);
  }

  function handleChangeValues(
    name: keyof AccountInfoFormType,
    searchQuery: string,
  ) {
    if (name === 'motoboyId') {
      debounceNameDeliveryManUpdate.current(searchQuery);
      return;
    }

    debouncePlaceUpdate.current(searchQuery);
  }

  return (
    <Form.Root
      className={styles.filtersForm}
      onSubmit={handleSubmit(submitForm)}
    >
      <div className={styles.filterGrid}>
        <Controller
          name="deliveryId"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextInput
              name="deliveryId"
              containerStyle={{
                alignSelf: 'flex-start',
                marginBottom: '1rem',
              }}
              value={value}
              onChange={onChange}
              label="ID da entrega"
              placeholder="entrega id"
              errorMessage={errors.deliveryId?.message}
            />
          )}
        />

        <Controller
          name="code"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextInput
              name="code"
              containerStyle={{
                alignSelf: 'flex-start',
                marginBottom: '1rem',
              }}
              value={value}
              onChange={onChange}
              label="Código"
              placeholder="código de entrega"
              errorMessage={errors.code?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="status"
          render={({ field: { onChange, value } }) => (
            <DropdownInput
              label="Status"
              options={STATUS_OPTIONS}
              loading={loadingCities}
              value={value as string[]}
              search
              multiple
              placeholder="Status"
              onChange={onChange}
            />
          )}
        />

        <Controller
          name="startDate"
          control={control}
          render={({ field: { onChange, value } }) => (
            <div>
              <CalendarInput
                name="startDate"
                value={value}
                label="Data inicial"
                selectsStart
                startDate={value}
                endDate={endDate}
                onChange={(newDate: Date | null) =>
                  handleChangeDate(newDate, onChange, 'startDate')
                }
                withPortal={isMobile}
                maxDate={new Date()}
                todayButton="Hoje"
                timeInputLabel="Horário:"
                showTimeInput
                popperPlacement="top"
              />
              {errors.startDate ? (
                <span className="text-error">{errors.startDate.message}</span>
              ) : null}
            </div>
          )}
        />

        <Controller
          name="endDate"
          control={control}
          render={({ field: { onChange, value } }) => (
            <div>
              <CalendarInput
                name="endDate"
                value={value}
                label="Data final"
                minDate={startDate}
                startDate={startDate}
                endDate={value}
                onChange={(newDate: Date | null) =>
                  handleChangeDate(newDate, onChange, 'endDate')
                }
                withPortal={isMobile}
                maxDate={new Date()}
                todayButton="Hoje"
                timeInputLabel="Horário:"
                showTimeInput
                popperPlacement="top"
              />
              {errors.endDate ? (
                <span className="text-error">{errors.endDate.message}</span>
              ) : null}
            </div>
          )}
        />

        <Controller
          name="cityId"
          control={control}
          render={({ field: { onChange, value } }) => (
            <DropdownInput
              clearable
              label="Município"
              options={CITIES_OPTIONS}
              loading={loadingCities}
              value={value}
              search
              placeholder="Município"
              onChange={value => {
                if (typeof value === 'string') {
                  dispatch(setFilterCityOnDeliveriesList(value));
                }
                onChange(value);
              }}
            />
          )}
        />

        <Controller
          name="placeId"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <DropdownInput
              loading={isFetching}
              label="Estabelecimento"
              value={value}
              search
              options={placeDropdownOptions}
              clearable
              noResultsMessage="..."
              onSearchChange={searchValue =>
                handleChangeValues(name, searchValue)
              }
              placeholder="Digite o nome"
              onChange={selectedValue => {
                onChange(selectedValue);
                const selectedPlace = placeDropdownOptions.find(
                  place => place.value === selectedValue,
                );
                setValue('placeName', selectedPlace?.text || '');
              }}
            />
          )}
        />

        <Controller
          name="motoboyId"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <DropdownInput
              loading={getMotoboys.isFetching}
              label="Entregador"
              value={value}
              search
              options={motoboyDropdownOptions}
              clearable
              noResultsMessage="..."
              onSearchChange={searchValue =>
                handleChangeValues(name, searchValue)
              }
              placeholder="Digite o nome"
              onChange={selectedValue => {
                onChange(selectedValue);
                const selectedMotoboy = motoboyDropdownOptions.find(
                  motoboy => motoboy.value === selectedValue,
                );
                setValue('motoboyName', selectedMotoboy?.text || '');
              }}
            />
          )}
        />

        <Controller
          name="deliveryType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <DropdownInput
              clearable
              label="Tipo de entrega"
              options={TYPE_DELIVERY_OPTIONS}
              value={value}
              placeholder="Selecionar filtro"
              onChange={value => {
                dispatch(
                  setFilterDeliveryTypeOnDeliveriesList(value as string),
                );
                onChange(value);
              }}
            />
          )}
        />

        <Controller
          name="billingType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <DropdownInput
              clearable
              label="Tipo de cobrança"
              options={BILLING_TYPE_OPTIONS}
              value={value}
              placeholder="Selecionar filtro"
              onChange={value => {
                onChange(value);
              }}
            />
          )}
        />

        <div className={styles.searchBtn}>
          <ButtonMain
            textFirst="buscar"
            firstStrong
            type="submit"
            disabled={isLoading}
            loading={isLoading}
          />
        </div>
      </div>
    </Form.Root>
  );
}
